import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useHttp } from 'hooks/use-fetch';
import { updateProviderGoal } from 'pages/Dashboard/services/freeTrial.service';
import {
  Typography,
  Box,
  TextField,
  Card,
  Button,
  useMediaQuery,
  Theme,
} from '@mui/material';
import CustomDrawer, { ActionButtonProps } from 'components/HLDrawer';
import theme from 'theme';
import { TrackChanges } from '@mui/icons-material';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

interface EditGoalDrawerProps {
  isOpen: boolean;
  closeDrawer: (open: boolean) => void;
  initialGoal: number;
  onSave: (goal: number) => void;
  leastGoal: number;
}

function EditGoalDrawer({
  isOpen,
  closeDrawer,
  initialGoal,
  onSave,
  leastGoal,
}: EditGoalDrawerProps) {
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  const [goal, setGoal] = useState<number>(initialGoal);
  const [error, setError] = useState<string>('');
  const { http, providerId } = useHttp();

  useEffect(() => {
    setGoal(initialGoal);
  }, [initialGoal]);

  const mutation = useMutation(
    (newGoal: number) => updateProviderGoal(http.put, providerId, String(newGoal)),
    {
      onSuccess: () => {
        onSave(goal);
        closeDrawer(false);
      },
      onError: (err: Error) => {
        setError(err?.message || 'Failed to update goal');
      },
    },
  );

  const handleSave = () => {
    trackMixpanelEvent(MixpanelEventName.DASHBOARD_EDIT_GOAL_MODAL_SAVE_BUTTON_CLICKED, { goal });
    if (goal < leastGoal) {
      setError(`Goal must be at least ${leastGoal}`);
    }
    else {
      setError('');
      mutation.mutate(goal);
    }
  };

  const onClose = () => {
    setError('');
    setGoal(initialGoal);
    closeDrawer(false);
  };

  // Single action button props
  const primaryAction: ActionButtonProps = {
    label: 'Save',
    ButtonProps: {
      variant: 'contained',
      color: 'primary',
      onClick: handleSave,
      sx: { mt: 1 },
      disabled: mutation.isLoading,
      fullWidth: !!isMobile,
    },
  };

  // Prepare content for CustomDrawer
  const drawerContent = (
    <Box>
      <Typography sx={{ mb: 3, color: theme.custom.colors.lightTextPrimary }}>
        Your patient activation goal is the number of patients you would like to add to Headlamp.
        <br />
        <br />
        You can adjust your goal at any time.
      </Typography>
      <Card sx={{ borderRadius: 2, p: 2, mb: 2 }}>
        <Box display='flex' alignItems='center' gap={1} mb={1}>
          <TrackChanges fontSize='small' sx={{ color: theme.custom.colors.lightSuccessMain }} />
          <Typography
            variant='subtitle1'
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              color: theme.custom.colors.lightTextPrimary,
            }}
          >
            Goal
          </Typography>
        </Box>
        <TextField
          label='Goal'
          type='number'
          value={goal}
          onChange={(e) => setGoal(Number(e.target.value))}
          error={!!error}
          helperText={error}
          sx={{ width: 120, m: 2 }}
          inputProps={{ min: 1 }}
          onFocus={(e) => e.target.select()}
        />
        <Typography
          variant='body2'
          sx={{ color: theme.custom.colors.lightTextSecondary, fontWeight: 500 }}
        >
          Recommended:
          {' '}
          <span style={{ fontWeight: 400 }}>set your goal to your total patient population</span>
        </Typography>
        {!isMobile && (
          <Button {...primaryAction.ButtonProps}>
            {primaryAction.label}
          </Button>
        )}
      </Card>
    </Box>
  );

  return (
    <CustomDrawer
      open={isOpen}
      onClose={onClose}
      title='Edit Patient Activation Goal'
      primaryAction={primaryAction}
    >
      {drawerContent}
    </CustomDrawer>
  );
}

export default EditGoalDrawer;
